import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, FileText, Package, ShoppingCart, Users, ShieldCheck,
  Bell, LogOut, Inbox, Boxes, BarChart3, Activity, Search, LifeBuoy, ScrollText, CreditCard, Power,
  Menu, X, AlertTriangle, FolderOpen, BellRing, BookOpen
} from "lucide-react";
import NotificationBell from "@/components/NotificationBell";

const navByRole = {
  buyer: [
    { to: "/app/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/app/browse", icon: Search, label: "Browse Products" },
    { to: "/app/rfqs/new", icon: FileText, label: "Create RFQ" },
    { to: "/app/rfqs", icon: Inbox, label: "My RFQs" },
    { to: "/app/orders", icon: ShoppingCart, label: "Orders" },
    { to: "/app/documents", icon: FolderOpen, label: "Documents" },
    { to: "/app/notifications", icon: BellRing, label: "Notifications" },
    { to: "/app/analytics", icon: BarChart3, label: "Analytics" },
    { to: "/app/tickets", icon: LifeBuoy, label: "Support" },
    { to: "/app/team", icon: Users, label: "Team" },
    { to: "/app/company", icon: ShieldCheck, label: "Company" },
  ],
  supplier: [
    { to: "/app/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/app/inbox", icon: Inbox, label: "RFQ Inbox" },
    { to: "/app/products", icon: Boxes, label: "Products" },
    { to: "/app/orders", icon: ShoppingCart, label: "Orders" },
    { to: "/app/documents", icon: FolderOpen, label: "Documents" },
    { to: "/app/notifications", icon: BellRing, label: "Notifications" },
    { to: "/app/analytics", icon: BarChart3, label: "Analytics" },
    { to: "/app/billing", icon: CreditCard, label: "Billing" },
    { to: "/app/tickets", icon: LifeBuoy, label: "Support" },
    { to: "/app/team", icon: Users, label: "Team" },
    { to: "/app/company", icon: ShieldCheck, label: "Company" },
  ],
  admin: [
    { to: "/app/dashboard", icon: LayoutDashboard, label: "Overview" },
    { to: "/app/notifications", icon: BellRing, label: "Notifications" },
    { to: "/app/analytics", icon: BarChart3, label: "Analytics" },
    { to: "/app/verifications", icon: ShieldCheck, label: "Verifications" },
    { to: "/app/all-rfqs", icon: FileText, label: "All RFQs" },
    { to: "/app/all-orders", icon: ShoppingCart, label: "All Orders" },
    { to: "/app/monetization", icon: Power, label: "Monetization" },
    { to: "/app/content", icon: BookOpen, label: "Content & Site" },
    { to: "/app/tickets", icon: LifeBuoy, label: "Tickets" },
    { to: "/app/audit", icon: ScrollText, label: "Audit Log" },
  ],
};

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const items = navByRole[user?.role] || [];
  const [open, setOpen] = React.useState(false);
  const isPending = user && (user.role === "buyer" || user.role === "supplier") && user.verification_status !== "approved";

  return (
    <div className="App dash-shell">
      {/* Mobile top bar (under 900px) */}
      <header className="dash-mobile-bar">
        <button className="btn btn-ghost dash-burger" onClick={() => setOpen(true)} data-testid="mobile-nav-open" aria-label="Open menu">
          <Menu size={20} />
        </button>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 26, height: 26, background: "var(--amber-500)", display: "grid", placeItems: "center", borderRadius: 2 }}>
            <Activity size={14} color="var(--navy-900)" strokeWidth={2.5} />
          </div>
          <div className="font-serif-display" style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)" }}>CanadaMRO</div>
        </Link>
        <NotificationBell />
      </header>

      {/* Drawer overlay */}
      {open && <div className="dash-drawer-overlay" onClick={() => setOpen(false)} />}

      <aside className={`dash-aside ${open ? "is-open" : ""}`}>
        <Link to="/" data-testid="brand-link" style={{ padding: "22px 20px", display: "flex", alignItems: "center", gap: 10, borderBottom: "1px solid var(--line)" }}>
          <div style={{ width: 32, height: 32, background: "var(--amber-500)", display: "grid", placeItems: "center", borderRadius: 2 }}>
            <Activity size={18} color="var(--navy-900)" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-serif-display" style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)", lineHeight: 1 }}>CanadaMRO</div>
            <div className="font-mono" style={{ fontSize: 10, color: "var(--ink-mute)", letterSpacing: ".1em", marginTop: 2 }}>EXCHANGE</div>
          </div>
          <button className="btn btn-ghost dash-drawer-close" onClick={() => setOpen(false)} aria-label="Close menu">
            <X size={18} />
          </button>
        </Link>

        <div style={{ padding: "14px 16px 8px", fontSize: 10, letterSpacing: ".1em", color: "var(--ink-mute)", textTransform: "uppercase", fontWeight: 600 }}>
          {user?.role} area
        </div>

        <nav style={{ flex: 1 }}>
          {items.map(({ to, icon: Icon, label }) => (
            <NavLink key={to} to={to} className="nav-link" data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`} onClick={() => setOpen(false)} end>
              <Icon size={16} /> {label}
            </NavLink>
          ))}
        </nav>

        <div style={{ padding: 16, borderTop: "1px solid var(--line)" }}>
          <div style={{ fontSize: 12, color: "var(--ink-dim)", marginBottom: 2 }}>{user?.full_name}</div>
          <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-mute)", marginBottom: 10, wordBreak: "break-all" }}>{user?.email}</div>
          <button className="btn btn-ghost" data-testid="logout-btn" onClick={async () => { await logout(); nav("/"); }} style={{ width: "100%", justifyContent: "center", fontSize: 12 }}>
            <LogOut size={14} /> Log out
          </button>
        </div>
      </aside>

      <main className="dash-main">
        <header className="dash-topbar">
          <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-mute)" }}>
            STATUS <span style={{ color: "var(--emerald)" }}>● LIVE</span> · ONTARIO · CAD
          </div>
          <NotificationBell />
        </header>

        {isPending && (
          <div data-testid="awaiting-approval-banner" className="awaiting-approval">
            <AlertTriangle size={16} color="var(--amber-400)" style={{ flexShrink: 0 }} />
            <div>
              <b style={{ color: "var(--amber-400)" }}>Account awaiting admin approval.</b>{" "}
              You can update your company profile and upload supporting documents, but you cannot create RFQs, submit quotes, or award orders until verified.
            </div>
          </div>
        )}

        <div className="dash-content fade-in-up">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
